import { useState } from 'react';
import {
  MdAccessTime,
  MdCheck,
  MdContentCopy,
  MdDownload,
  MdLogout,
  MdPlayArrow,
} from 'react-icons/md';
import { Navigate, useLocation } from 'react-router-dom';
import { AppHeader } from '../components/AppHeader';
import { useSession } from '../context/SessionContext';
import { downloadCustomerTranscriptPdf } from '../lib/session-transcript-pdf';
import { TranscriptAvatar } from '../components/TranscriptAvatar';
import { customerAssistantDisplayName } from '../lib/customerCopy';
import { pathWithKeptQuery } from '../lib/sessionUrl';

function formatDuration(sec: number) {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function CompletePage() {
  const {
    profile,
    agentInfo,
    messages,
    sessionId,
    shortId,
    durationSec,
    hasCompletedSession,
    toast,
    showToast,
    copyShortId,
    onNewSession,
    onLeave,
  } = useSession();
  const location = useLocation();

  const [copyHint, setCopyHint] = useState('Copy');
  const [downloadBusy, setDownloadBusy] = useState(false);

  if (!hasCompletedSession && !sessionId) {
    return <Navigate to={pathWithKeptQuery('/haircheck-returning', location.search)} replace />;
  }

  const firstName = profile?.firstName || 'User';
  const lastName = profile?.lastName || 'Unknown';
  const displayName = `${firstName} ${lastName}`.trim();
  const agentName = customerAssistantDisplayName(agentInfo?.name);
  const durationLabel = formatDuration(durationSec);
  const displayId = shortId || sessionId.slice(-8) || sessionId;
  const showTranscript = agentInfo?.completePageTranscriptEnabled !== false;

  async function onCopy() {
    await copyShortId();
    setCopyHint('Copied');
    window.setTimeout(() => setCopyHint('Copy'), 1400);
  }

  async function downloadTranscript() {
    if (!showTranscript || downloadBusy) return;
    setDownloadBusy(true);
    try {
      await downloadCustomerTranscriptPdf({
        sessionId,
        displayId,
        participantName: displayName,
        durationLabel,
        agentInfo,
        messages,
      });
      showToast('Transcript downloaded');
    } catch (e) {
      console.error(e);
      showToast('Could not create PDF');
    } finally {
      setDownloadBusy(false);
    }
  }

  return (
    <div className="complete-page">
      <AppHeader greeting={`Hi, ${displayName}`} />

      <main className={`complete-shell${showTranscript ? '' : ' complete-shell--summary-only'}`}>
        <section className="summary-card surface" aria-labelledby="completeTitle">
          <div className="success-mark" aria-hidden="true">
            <MdCheck />
          </div>
          <h1 id="completeTitle">Session complete</h1>
          <p className="summary-intro">
            Your conversation has been saved
            <br />
            and is ready to review.
          </p>
          <div className="summary-divider" aria-hidden="true" />

          <div className="duration-row">
            <MdAccessTime aria-hidden="true" />
            <span>Duration</span>
            <strong>{durationLabel}</strong>
          </div>

          <div className="session-field">
            <label htmlFor="sessionId">Session ID</label>
            <div className="session-id-box">
              <span id="sessionId">{displayId}</span>
              <button
                className="copy-button"
                type="button"
                aria-label="Copy session ID"
                onClick={() => void onCopy()}
              >
                <span className="copy-hint" aria-hidden="true">
                  {copyHint}
                </span>
                <MdContentCopy aria-hidden="true" />
              </button>
            </div>
          </div>

          <aside className="next-steps" aria-labelledby="nextStepsTitle">
            <span className="next-steps-icon" aria-hidden="true">
              i
            </span>
            <div>
              <h2 id="nextStepsTitle">What happens next</h2>
              <p>
                Our team will review your conversation and follow up by email within 1–2 business
                days with recommendations and next steps.
              </p>
            </div>
          </aside>

          <div className="summary-actions">
            {showTranscript ? (
              <button
                className="secondary-action"
                type="button"
                disabled={downloadBusy}
                onClick={() => void downloadTranscript()}
              >
                <MdDownload aria-hidden="true" />
                <span>{downloadBusy ? 'Preparing PDF…' : 'Download transcript'}</span>
              </button>
            ) : null}
            <button className="summary-primary" type="button" onClick={onNewSession}>
              <MdPlayArrow aria-hidden="true" />
              <span>Start new session</span>
            </button>
          </div>

          <div className="logout-row">
            <button className="logout-action" type="button" onClick={() => void onLeave()}>
              <MdLogout aria-hidden="true" />
              <span>Log out</span>
            </button>
          </div>
        </section>

        {showTranscript ? (
        <section
          className="transcript-card surface"
          aria-labelledby="transcriptTitle"
          tabIndex={0}
          aria-label="Scrollable session transcript"
        >
          <header className="transcript-header">
            <h1 id="transcriptTitle">Transcript</h1>
            <p>
              {durationLabel}&nbsp;&nbsp;·&nbsp;&nbsp;{messages.length} message
              {messages.length === 1 ? '' : 's'}
            </p>
          </header>

          <div className="transcript-scroll" id="transcriptScroll">
            {messages.length === 0 ? (
              <p style={{ padding: 16, color: '#7182b5' }}>No transcript messages for this session.</p>
            ) : (
              messages.map((m) => {
                const isAgent = m.author === 'Agent';
                return (
                  <article
                    key={m.key}
                    className={`message ${isAgent ? 'agent-message' : 'user-message'}`}
                  >
                    <TranscriptAvatar
                      variant={isAgent ? 'agent' : 'user'}
                      agentName={agentName}
                      agentAvatarUrl={agentInfo?.avatarUrl}
                    />
                    <div className="message-body">
                      <div className="message-meta">
                        <strong>{isAgent ? agentName : 'You'}</strong>
                        <span>·</span>
                        <time>{m.time}</time>
                      </div>
                      <p className="message-bubble">{m.text}</p>
                    </div>
                  </article>
                );
              })
            )}
          </div>
        </section>
        ) : null}
      </main>

      <div className="toast" role="status" aria-live="polite" hidden={!toast}>
        {toast}
      </div>
    </div>
  );
}
